import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChange, SimpleChanges } from '@angular/core';
import { EChartsOption } from 'echarts';
import { Subscription } from 'rxjs';
import { DataService, ServicecallService } from 'src/app';
import { ChartAttributes } from 'src/app/model/shared/components/entities/entities-profile/chart-builder';
import { EntitiesServiceService } from 'src/app/shared/services';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { AnalyticsServiceService } from 'src/app/modules/analytics/analytics-service.service';

@Component({
  selector: 'app-chart-builder',
  templateUrl: './chart-builder.component.html',
  styleUrl: './chart-builder.component.css'
})
export class ChartBuilderComponent implements OnInit, OnChanges {
  combinedRuleResults: { rule1: any[]; rule2: any[]; rule3: any[]; rule4: any[]; controlLimits: { mean: number; upperControl3: number; lowerControl3: number; upperControl2: number; lowerControl2: number; upperControl1: number; lowerControl1: number; }; };
  loaderStopped: boolean;

  @Input()
  set entityDetails(value: any) {
    this._entityDetails = value;
  }

  get entityDetails() {
    return this._entityDetails;
  }

  @Input() showWER: boolean = false;
  @Input() pageType: string;
  @Input() hideStatistics: boolean = false;
  @Input() selfServiceChartAttributes: {
    chartAttributes: ChartAttributes, selectedDates: {
      from: Date,
      to: Date,
    }, details?: { _id: string },
    order?: number
  };
  @Input() captureImage: boolean = false;
  @Input() expand : boolean;
  @Input() addMarker : boolean = false;

  selectedDates: {
    from: Date,
    to: Date,
  }
  chartAttributes: ChartAttributes;
  apiHitTime: Date;
  isloader: boolean = true;
  entityId: string;

  //Attribute variables
  attributeData: any;
  statisticsAttributeData: any;
  statsSplitY1: { name: string, value: string | number }[] = [];
  statsSplitY2: { name: string, value: string | number }[] = [];
  private _tenantConfig: any;
  private _werThresholds: { mean: number, standardDeviation: number } = { mean: null, standardDeviation: null };

  //Subscription
  private _getEntityAttributeSubscription: Subscription;
  private _subscription: Subscription;
  private _entityDetails: any

  //Chart
  chartData: any;
  selectedChartType: 'line' | 'doubleY' | 'scatter' | 'doubleScatter' = 'line';
  showRules = true;
  showCombinedAnalysis = true;
  allSeriesData = [];

  //Table
  switchToTable: boolean = false;
  columnHeader: any = {};
  chartDataMaster: any;
  isTableLoading: boolean = false;

  // Store the combined data sorted by x-value (for chronological analysis)
  combinedData = [];

  constructor(public service: ServicecallService, private _datePipe: DatePipe, private _dataSer: DataService, private _entityService: EntitiesServiceService, private _analyticsService: AnalyticsServiceService) {
    this._subscription = this._dataSer.events.subscribe((res: any) => {
      if (res.name === 'siteChanged' || res.name === 'apiRefresh' || res.name === 'trendUpdate') {
        this.loaderStopped = false;
        if (this.chartAttributes && this.chartAttributes?.chartType && this.selectedDates && !this.addMarker) {
          this._getAttributeDetails();
        }
      } else if (res.name === 'plotChart') {
        this.chartAttributes = res?.args?.[0]?.chartAttributes;
        this.selectedDates = res?.args?.[0]?.selectedDates;
        this.entityId = res?.args?.[0]?.details?._id ?? null;
        this.statsSplitY1 = [];
        this.statsSplitY2 = [];
        if (this.chartAttributes && this.chartAttributes?.chartType && this.selectedDates) {
          this._getAttributeDetails();
        }
      }
    });
    setTimeout(() => {
      this._dataSer.broadcast('requestChartData', {});
    }, 100);
  }

  ngOnInit(): any {
    this._tenantConfig = this.service.getConfigurationBasedOnCategory(this.entityDetails) ?
      this.service.getConfigurationBasedOnCategory(this.entityDetails) : [];
    this.statsSplitY1 = this._tenantConfig?.attributesChartLegends?.length ? this._tenantConfig?.attributesChartLegends : [];
  }

  ngOnChanges(changes: SimpleChanges): any {
    this.loaderStopped = false;
    if(changes['showWER'] || changes['selfServiceChartAttributes'] || changes['pageType'] || changes['hideStatistics'] || changes['captureImage'] || changes['entityDetails']) {
      if (this.selfServiceChartAttributes) {
        this.chartAttributes = this.selfServiceChartAttributes?.chartAttributes;
        this.selectedDates = this.selfServiceChartAttributes?.selectedDates;
        this.entityId = this.selfServiceChartAttributes?.details?._id ?? null;
      }
      if (this.chartAttributes && this.selectedDates) {
        this._werThresholds = this.service.getConfiguration('tenantGenericConfig')?.analytics?.selfService?.wer?.[this.chartAttributes?.yAxis?.name] ?? null;
        if(!this.expand) {
          this._getAttributeDetails();
        } else {
          this.processData(this._analyticsService.getChartList()?.find(item => item.order === this.selfServiceChartAttributes?.order)?.attributeData);
        }
      }
    }
  }

  reloadApi() {
    this.loaderStopped = false;
    this.isloader = true;
    if (this.chartAttributes && this.selectedDates) {
      this._werThresholds = this.service.getConfiguration('tenantGenericConfig')?.analytics?.selfService?.wer?.[this.chartAttributes?.yAxis?.name] ?? null;
      if(!this.expand) {
        this._getAttributeDetails();
      } else {
        this.processData(this._analyticsService.getChartList()?.find(item => item.order === this.selfServiceChartAttributes?.order)?.attributeData);
      }
    }
  }

  stopLoading() {
    this.loaderStopped = true;
    this._analyticsService.removeChart(this.selfServiceChartAttributes?.order);
    this._getEntityAttributeSubscription?.unsubscribe();
  }

  ngOnDestroy() {
    this._getEntityAttributeSubscription?.unsubscribe();
    this._subscription?.unsubscribe();
  }

  private _getRequestData() { //Check & make change only for self service
    return {
      source: 'heartbeat',
      entityIds: this.pageType === 'selfService' ? this.entityId : [this.entityDetails?._id],
      from: this._datePipe.transform(this.selectedDates?.from, 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(this.selectedDates?.to, 'yyyy-MM-ddTHH:mm:ssZZ'),
      siteId: this.service.getSiteId(),
      attributes: this.chartAttributes && Object.keys(this.chartAttributes)?.length ? (this.chartAttributes?.yAxis2?.name ? [this.chartAttributes?.xAxis?.name, this.chartAttributes?.yAxis?.name, this.chartAttributes?.yAxis2?.name] : [this.chartAttributes?.xAxis?.name, this.chartAttributes?.yAxis?.name]) : [],
      includeHistory: false
    };
  }

  private _getAttributeDetails() {
    this.isloader = true;
    this.apiHitTime = new Date();
    this._getEntityAttributeSubscription = this.service.getEntityAttributeV1(this._getRequestData()).subscribe({
      next: (resp: any) => {
        const attrData = { ...resp }; //Origial data without reverse
        this.attributeData = resp; //Changeeee
        this.processData(attrData);
      },
      error: () => {
        this.isloader = false;
        this.attributeData = [];
        this.statisticsAttributeData = {};
      }
    })
  }

  processData(attrData) {
    this.isloader = false;
    this.attributeData = Object.keys(attrData).reduce((acc, element) => {
      acc[element] = Array.isArray(attrData[element])
        ? [...attrData[element]]?.reverse()
        : attrData[element];
      return acc;
    }, {});
    const widgetData = {attributeData : this.attributeData, order: this.selfServiceChartAttributes?.order};
    this._analyticsService.setChartList(widgetData);
    let colHeader = { 'time': 'Time', 'entityUniqueId': 'Chassis Number' };
    let axisDetails = [];
    axisDetails.push(this.chartAttributes?.xAxis, this.chartAttributes?.yAxis, this.chartAttributes?.yAxis2);
    const columns = axisDetails?.reduce((acc, item) => {
      if (item?.name != null && item?.attributeName != null && item?.name !== "sourceTimestamp") {
        acc[item.name] = item?.attributeName;
      }
      return acc;
    }, {});
    colHeader = { ...colHeader, ...columns };
    this.columnHeader = colHeader;
    const merged = [];

    Object.keys(attrData).forEach(key => {
      attrData?.[key]?.forEach(({ entityId, value, timestamp }) => {
        let existing = merged.find(item => item.entityId === entityId && item.timestamp === timestamp && (item[key] === undefined || item[key] === value));

        if (!existing) {
          existing = {
            entityId,
            time: new DatePipe('en-US').transform(timestamp, 'medium'),
            timestamp,
            entityUniqueId:
              this.chartAttributes?.entityNames?.find(e => e?._id === entityId)?.uniqueId ??
              entityId
          };
          merged.push(existing);
        }
        existing[key] = value;
      });
    });
    this.chartDataMaster = merged;
    this._plotAttributeGraph();
    if (attrData?.status) {
      if (attrData?.response?.data) {
        this.attributeData = attrData?.response?.data?.attributes;
        //Reversing the attribute array since new values are present in 0th position.
        this.attributeData = Object.keys(this.attributeData).reduce((acc, element) => {
          acc[element] = Array.isArray(this.attributeData[element])
            ? [...this.attributeData[element]]?.reverse()
            : this.attributeData[element];
          return acc;
        }, {});
        if (Object.keys(this.attributeData).length) {
          this._calculateStatistics(this.attributeData?.[this.chartAttributes?.yAxis?.name]);
          this._plotAttributeGraph();
        } else {
          this.attributeData = {};
          this.statisticsAttributeData = {};
          this._plotAttributeGraph();
        }
      } else {
        this.attributeData = {};
        this.statisticsAttributeData = {};
        this._plotAttributeGraph();
      }
    }
    this.isloader = false;
  }

  private _plotAttributeGraph() {
    const { chartType, yAxis, yAxis2, xAxis } = this.chartAttributes || {};
    const chartTypeValue = chartType?.value;
    const hasYAxis = !!yAxis?.attributeName;
    const hasYAxis2 = !!yAxis2?.attributeName;

    if (!chartTypeValue || !hasYAxis) return;

    const isLineChart = chartTypeValue === 'line';
    const isScatterChart = chartTypeValue === 'scatter';
    const plotTime = xAxis?.name === 'sourceTimestamp';

    if (isLineChart) {
      this.selectedChartType = hasYAxis2 ? 'doubleY' : 'line';
      if (hasYAxis2) {
        this._setDoubleLineChartContent(plotTime);
        this.statsSplitY2 = this._calculateStatistics(this.attributeData?.[this.chartAttributes?.yAxis2?.name])
      } else {
        this._setLineChartContent(plotTime);
      }
    }

    if (isScatterChart) {
      this.selectedChartType = hasYAxis2 ? 'doubleScatter' : 'scatter';
      const chartData1 = plotTime ? this.attributeData?.[this.chartAttributes?.yAxis?.name]?.map(item => [new Date(item.timestamp), item.value]) : this.mapMatchingAttributes(this.attributeData, xAxis?.name, yAxis?.name);
      if (hasYAxis2) {
        const chartData2 = plotTime ? this.attributeData?.[this.chartAttributes?.yAxis2?.name]?.map(item => [new Date(item.timestamp), item.value]) : this.mapMatchingAttributes(this.attributeData, xAxis?.name, yAxis2?.name);
        this.statsSplitY2 = this._calculateStatistics(this.attributeData?.[this.chartAttributes?.yAxis2?.name]);
        this._setDoubleScatterChartContent(chartData1, chartData2, plotTime);
      } else {
        this._setScatterChartContent(chartData1, plotTime);
      }
    }
    this.statsSplitY1 = this._calculateStatistics(this.attributeData?.[this.chartAttributes?.yAxis?.name]);
  }

  private _calculateStatistics(attributeData) {
    let sumValue: any = 0;
    let meanValue: any;
    attributeData?.map(({ value }) => value)?.forEach(value => {
      sumValue += parseFloat(value);
    });
    if (sumValue) {
      meanValue = sumValue / (attributeData?.map(({ value }) => value))?.length;
    }
    return [{ name: 'Computed Mean', value: meanValue }, { name: 'Current Value', value: attributeData?.map(({ value }) => value)?.[attributeData?.length - 1] }];
  }

  dataZoom(event) {
    console.log(event)
  }


  private _setLineChartContent(plotTime: boolean) {
    const seriesData = [];
    const entityIds = [...new Set(this.pageType === 'selfService' ? this.entityId : [this.entityDetails?._id])];

    let xData;
    this.allSeriesData = [];
    let showDecimals = false;

    entityIds.forEach((entityId, i) => {
      // Find corresponding X values (TotalDistance)
      if (plotTime) {
        xData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => new Date(item.timestamp));
      } else {
        xData = this.attributeData?.[this.chartAttributes?.xAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      }

      const yData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      const allZeroDecimal = yData?.length ? yData?.every(v => v >=0 && v < 1) : false;
      if (allZeroDecimal) {
        showDecimals = true;
      }
      this.allSeriesData.push(xData?.map((x, index) => ([x, yData?.[index], i, this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId, this.chartAttributes?.xAxis?.attributeName, this.chartAttributes?.yAxis?.attributeName])));
    })

    this.combineAndSortData();
    this.combinedRuleResults = this.applyWesternElectricRulesToCombined();

    entityIds.forEach(entityId => {
      let xData;
      // Find corresponding X values (TotalDistance)
      if (plotTime) {
        xData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => new Date(item.timestamp));
      } else {
        xData = this.attributeData?.[this.chartAttributes?.xAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      }

      // Find corresponding Y values (VehicleSpeed)
      const yData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));

      // if (xData?.length !== yData?.length) {
      //   console.error(`Data mismatch for entity ${entityId}: xData and yData lengths don't match.`);
      //   return;
      // }

      if (xData?.length === 0 || yData?.length === 0) {
        console.log(`Skipping empty data for entityId: ${entityId}`);
        return; // Skip empty data series
      }


      // Push the series data
      seriesData.push({
        name: this.chartAttributes?.yAxis?.uom ? `${this.chartAttributes?.yAxis?.attributeName} (${this.chartAttributes?.yAxis?.uom})` : this.chartAttributes?.yAxis?.attributeName,
        type: 'line',
        data: xData?.map((x, index) => ({
          value: [x, yData?.[index]],
          entityId: entityId,
          entityName: this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId,
          xAxisName: this.chartAttributes?.xAxis?.attributeName,
          yAxisName: this.chartAttributes?.yAxis?.attributeName
        })), // Combine X and Y data
        connectNulls: true,
        markPoint: {
          tooltip: {
            show: false
          },
          // data: this.pageType !== 'selfService' && [
          //   { type: 'max', name: 'Max Value' },
          //   { type: 'min', name: 'Min Value' }
          // ],
          label: {
            formatter: function (param: any) {
              return param.data.name + ': ' + param.value?.toFixed(3);
            }
          },
          symbolSize: 50
        },
        markLine: {
          data: [
            ...(this.chartAttributes?.yAxis?.markers?.max
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.yAxis?.markers?.min
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.min }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.max
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.min
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.min }]
              : [])
          ],
          lineStyle: {
            type: 'solid',
            color: 'rgba(250, 174, 165, 1)',
            width: 1
          },
          symbol: 'none',
          label: {
            show: false
          },
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
        },
        lineStyle: {
          color: 'rgba(50, 165, 199, 1)', // Change line color
          width: 2, // Adjust line thickness
        },
        itemStyle: {
          color: '#1E8C9B', // Change point color (green)
        },
        sampling: 'none', // Prevents downsampling
        showSymbol: true,  // Forces markers to be displayed
        showAllSymbol: true,  // Ensures markers on all points
        large: false, // Disables large data optimization
        symbol: 'circle', // Ensure all points have markers
        symbolSize: 5, // Set a fixed marker size
        emphasis: {
          focus: 'series'
        }
      });


      if (this.showRules && this.showCombinedAnalysis && this.combinedRuleResults && this.pageType === 'selfService' && this.showWER) {
        const limits = this.combinedRuleResults?.controlLimits;

        seriesData.push({
          name: '',
          type: 'line',
          symbol: 'none',
          lineStyle: {
            opacity: 0 // Make the line invisible
          },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: {
              color: '#333',
              type: 'dashed'
            },
            data: [
              { yAxis: limits.mean, lineStyle: { color: 'rgba(29, 29, 29, 0.4)', type: 'solid', width: 0.8 } },
              { yAxis: limits.upperControl3, lineStyle: { color: '#FF0000CF', type: 'solid', width: 0.8 } },
              { yAxis: limits.lowerControl3, lineStyle: { color: '#FF0000CF', type: 'solid', width: 0.8 } },
              { yAxis: limits.upperControl2, lineStyle: { color: '#FFBB00', type: 'solid', width: 0.8 } },
              { yAxis: limits.lowerControl2, lineStyle: { color: '#FFBB00', type: 'solid', width: 0.8 } },
              { yAxis: limits.upperControl1, lineStyle: { color: '#00D764', type: 'solid', width: 0.8 } },
              { yAxis: limits.lowerControl1, lineStyle: { color: '#00D764', type: 'solid', width: 0.8 } }
            ],
            label: {
              show: true,
              formatter: function (params) {
                if (params.value === Math.round(limits.mean * 100) / 100) return 'μ';
                if (params.value === Math.round(limits.upperControl3 * 100) / 100) return '+3σ';
                if (params.value === Math.round(limits.lowerControl3 * 100) / 100) return '-3σ';
                if (params.value === Math.round(limits.upperControl2 * 100) / 100) return '+2σ';
                if (params.value === Math.round(limits.lowerControl2 * 100) / 100) return '-2σ';
                if (params.value === Math.round(limits.upperControl1 * 100) / 100) return '+1σ';
                if (params.value === Math.round(limits.lowerControl1 * 100) / 100) return '-1σ';
                return '';
              }
            }
          }
        });
      }
    });


    if (this.showRules && this.showCombinedAnalysis && this.combinedRuleResults && this.pageType === 'selfService' && this.showWER) {
      // Separate rule violations by series
      // const rule1BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule1, entityIds?.length);
      // const rule2BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule2, entityIds?.length);
      // const rule3BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule3, entityIds?.length);
      // const rule4BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule4, entityIds?.length);

      // Add rule violation series if they contain points for this series
      // if (this.combinedRuleResults.rule1?.length > 0) {
      seriesData.push({
        name: `WER1`,
        type: 'line',
        symbol: 'rect',
        symbolSize: 12,
        itemStyle: {
          color: 'transparent',
          borderColor: '#CF0A0A',
          borderWidth: 2
        },
        data: this.combinedRuleResults.rule1 ?? []
      });
      // }

      // // if (this.combinedRuleResults.rule2?.length > 0) {
      // seriesData.push({
      //   name: `WER2`,
      //   type: 'line',
      //   symbol: 'circle',
      //   symbolSize: 18,
      //   itemStyle: {
      //     color: 'transparent',
      //     borderColor: '#3A5AFE',
      //     borderWidth: 0.8
      //   },
      //   data: this.combinedRuleResults.rule2 ?? []
      // });
      // // }

      // // if (this.combinedRuleResults.rule3?.length > 0) {
      // seriesData.push({
      //   name: `WER3`,
      //   type: 'line',
      //   symbol: 'circle',
      //   symbolSize: 14,
      //   itemStyle: {
      //     color: 'transparent',
      //     borderColor: '#C364C3',
      //     borderWidth: 0.8
      //   },
      //   data: this.combinedRuleResults.rule3 ?? []
      // });
      // // }

      // // if (this.combinedRuleResults.rule4?.length > 0) {
      // seriesData.push({
      //   name: `WER4`,
      //   type: 'line',
      //   symbol: 'circle',
      //   symbolSize: 10,
      //   itemStyle: {
      //     color: 'transparent',
      //     borderColor: '#10A957',
      //     borderWidth: 0.8
      //   },
      //   data: this.combinedRuleResults.rule4 ?? []
      // });
      // // }
    }

    //To align the chart y-axis range within max & min values
    const allYValues = this.allSeriesData?.flat()?.filter(point => Array.isArray(point) && point.length > 1)?.map(point => point[1]);
    const maxVal = Math.max(...[...allYValues, this.combinedRuleResults?.controlLimits?.upperControl3]);
    const minVal = Math.min(...[...allYValues, this.combinedRuleResults?.controlLimits?.lowerControl3]);

    const padding = (maxVal - minVal) * 0.05; // 5% of the range

    const yvaluesMax = maxVal + padding;
    const yvaluesMin = minVal - padding;

    this.chartData = {
      title: {
        text: this.chartAttributes?.name,
        top: 0,
        left: '3%',
        textStyle: {
          fontSize: 13,
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.87)',
          fontFamily: '"Roboto Flex", sans-serif'
        }
      },
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: '#A9A9A9', // Darker grey color
            width: 2, // Line width
            type: 'dashed'
          }
        },
        position: function (point) {
          const [x, y] = point;

          return [x + 30, y];  // Add 10px to the x-coordinate to move the tooltip to the right
        },
        formatter: function (params) {
          const dataPoint = params || {};
          const data = dataPoint.data || null;

          if (!data) {
            return 'No data available';
          }

          let xValue, yValue, entName, xName, yName;

          if (Array.isArray(data)) {
            // If data is an array, destructure it as [x, y]
            [xValue, yValue, entName, xName, yName] = data;
          } else {
            // If data is an object, assume it has `x` and `y` properties
            xValue = data?.value?.[0];
            yValue = data?.value?.[1];
            entName = data?.value?.[3];
            xName = data?.value?.[4];
            yName = data?.value?.[5];
          }
          const entityName = data?.entityName ?? data?.[3]; // Access the entityId from the metadata
          const xAxisName = data?.xAxisName ?? data?.[4];
          const yAxisName = data?.yAxisName ?? data?.[5];

          return `
            Entity ID: ${entityName}<br/>
            ${xAxisName}: ${plotTime ? new Date(xValue)?.toLocaleString('en-US', {
            weekday: 'long', // "Monday"
            year: 'numeric', // "2025"
            month: 'long', // "March"
            day: 'numeric', // "13"
            hour: '2-digit', // "02"
            minute: '2-digit', // "30"
            second: '2-digit', // "15"
            hour12: true, // "AM/PM"
          }) : xValue}<br/>
            ${yAxisName}: ${yValue}
          `;
        },
        appendToBody: true
      },
      grid: {
        containLabel: true,
        top: '28%'
      },
      legend: {
        top: '7%',
        show: true,
        selectedMode: true
      },
      xAxis: {
        type: plotTime ? 'time' : 'value',
        // data: plotTime ? undefined : this.attributeData?.[this.chartAttributes?.xAxis?.name]?.map(item => item.value), // x-axis values
        name: this.chartAttributes?.xAxis?.uom ? `${this.chartAttributes?.xAxis?.attributeName} (${this.chartAttributes?.xAxis?.uom})` : this.chartAttributes?.xAxis?.attributeName,
        nameLocation: 'middle',
        nameTextStyle: {
          fontWeight: 'bold',
          padding: [10, 0, 0, 0]
        },
        nameGap: 30,
        axisLabel: {
          formatter: function (value) {
            if (!plotTime) return value;

            const date = new Date(value);
            const time = date.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
            const formattedDate = date.toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric'
            });
            return `${time}\n${formattedDate}`;
          },
          hideOverlap: true
        },
        hideOverlap: true
      },
      yAxis: {
        type: 'value',
        name: 'Value',
        scale: true,
        min: yvaluesMin,
        max: yvaluesMax,
        axisLabel: {
          formatter: (value: number) => {
            return showDecimals ? value?.toFixed(2)?.toString() : Math.floor(value)?.toString();
          },
          textStyle: {
            fontSize: 12,
            fontFamily: 'Arial'
          }
        }
      },
      toolbox: {
        left: '5%',
        top: '5%',
        show: true,
        feature: {
          dataZoom: {
            show: true,
            icon: {
              back: ' '
            }
          },
          restore: {},
          brush: {
            show: false // Disables the brush feature
          },
          ...(this.captureImage && { saveAsImage: { title: 'Download', name: this.chartAttributes?.name ?? 'Download' } }),
          ...(this.captureImage && {
            myTableSwitch: {
              show: true,
              title: 'Switch to Table',
              icon: 'path://M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z',
              onclick: (chartInstance) => {
                this.isTableLoading = true;
                this.switchToTable = true;
              }
            }
          })
        }
      },
      series: seriesData
    }
  }

  private _setDoubleLineChartContent(plotTime: boolean) {
    const seriesData = [];
    const entityIds = [...new Set(this.pageType === 'selfService' ? this.entityId : [this.entityDetails?._id])];
    let xData;

    entityIds.forEach(entityId => {
      // Find corresponding X values (TotalDistance)
      if (plotTime) {
        xData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => new Date(item.timestamp));
      } else {
        xData = this.attributeData?.[this.chartAttributes?.xAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      }

      // Find corresponding Y values (VehicleSpeed)
      const yData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));

      const y2Data = this.attributeData?.[this.chartAttributes?.yAxis2?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));

      // if (xData?.length !== yData?.length) {
      //   console.error(`Data mismatch for entity ${entityId}: xData and yData lengths don't match.`);
      //   return;
      // }

      if (xData?.length === 0 || yData?.length === 0 || y2Data?.length === 0) {
        console.log(`Skipping empty data for entityId: ${entityId}`);
        return; // Skip empty data series
      }

      // Push the series data
      seriesData.push({
        name: this.chartAttributes?.yAxis?.uom ? `${this.chartAttributes?.yAxis?.attributeName} (${this.chartAttributes?.yAxis?.uom})` : this.chartAttributes?.yAxis?.attributeName,
        type: 'line',
        data: xData?.map((x, index) => ({
          value: [x, yData?.[index]],
          entityId: entityId,
          entityName: this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId,
          xAxisName: this.chartAttributes?.xAxis?.attributeName,
          yAxisName: this.chartAttributes?.yAxis?.attributeName
        })),  // Combine X and Y data
        connectNulls: true,
        yAxisIndex: 0,
        markPoint: {
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
          // data: this.pageType !== 'selfService' && [
          //   { type: 'max', name: 'Max Value' },
          //   { type: 'min', name: 'Min Value' }
          // ],
          label: {
            formatter: function (param: any) {
              return param.data.name + ': ' + param.value?.toFixed(3);
            }
          },
          symbolSize: 50
        },
        markLine: {
          data: [
            ...(this.chartAttributes?.yAxis?.markers?.max
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.yAxis?.markers?.min
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.min }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.max
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.min
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.min }]
              : [])
          ],
          lineStyle: {
            type: 'solid',
            color: 'rgba(250, 174, 165, 1)',
            width: 1
          },
          symbol: 'none',
          label: {
            show: false
          },
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
        },
        lineStyle: {
          color: 'rgba(50, 165, 199, 1)', //   Change line color 
          width: 1, // Adjust line thickness
        },
        itemStyle: {
          color: '#055c7b', //   Change point color (green)
        },
        sampling: 'none', // Prevents downsampling
        showSymbol: true,  // Forces markers to be displayed
        showAllSymbol: true,  // Ensures markers on all points
        large: false, // Disables large data optimization
        symbol: 'circle', // Ensure all points have markers
        symbolSize: 5, // Set a fixed marker size
        emphasis: {
          focus: 'series'
        }
      });

      seriesData.push({
        name: this.chartAttributes?.yAxis2?.uom ? `${this.chartAttributes?.yAxis2?.attributeName} (${this.chartAttributes?.yAxis2?.uom})` : this.chartAttributes?.yAxis2?.attributeName,
        type: 'line',
        data: xData?.map((x, index) => ({
          value: [x, y2Data?.[index]],
          entityId: entityId,
          entityName: this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId,
          xAxisName: this.chartAttributes?.xAxis?.attributeName,
          yAxisName: this.chartAttributes?.yAxis2?.attributeName
        })),  // Combine X and Y data
        connectNulls: true,
        yAxisIndex: 1,
        markPoint: {
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
          // data: this.pageType !== 'selfService' && [
          //   { type: 'max', name: 'Max Value' },
          //   { type: 'min', name: 'Min Value' }
          // ],
          label: {
            formatter: function (param: any) {
              return param.data.name + ': ' + param.value?.toFixed(3);
            }
          },
          symbolSize: 50
        },
        markLine: {
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
          data: [
            ...(this.chartAttributes?.yAxis2?.markers?.max
              ? [{ yAxis: this.chartAttributes?.yAxis2?.markers?.max }]
              : []),
            ...(this.chartAttributes?.yAxis2?.markers?.min
              ? [{ yAxis: this.chartAttributes?.yAxis2?.markers?.min }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.max
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.min
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.min }]
              : [])
          ],
          lineStyle: {
            type: 'solid',
            color: 'rgba(250, 174, 165, 1)',
            width: 1
          },
          symbol: 'none',
          label: {
            show: false
          }
        },
        lineStyle: {
          color: '#808080', //   Change line color 
          width: 1, // Adjust line thickness
        },
        itemStyle: {
          color: '#8A9A5B', //   Change point color (green)
        },
        sampling: 'none', // Prevents downsampling
        showSymbol: true,  // Forces markers to be displayed
        showAllSymbol: true,  // Ensures markers on all points
        large: false, // Disables large data optimization
        symbol: 'circle', // Ensure all points have markers
        symbolSize: 5, // Set a fixed marker size
        emphasis: {
          focus: 'series'
        }
      });
    });
    this.chartData = {
      title: {
        text: this.chartAttributes?.name,
        top: 0,
        left: '3%',
        textStyle: {
          fontSize: 13,
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.87)',
          fontFamily: '"Roboto Flex", sans-serif'
        }
      },
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: '#A9A9A9', // Darker grey color
            width: 2, // Line width
            type: 'dashed'
          }
        },
        position: function (point) {
          const [x, y] = point;

          return [x + 30, y];  // Add 10px to the x-coordinate to move the tooltip to the right
        },
        formatter: function (params) {
          const dataPoint = params || {};
          const data = dataPoint.data || null;

          if (!data) {
            return 'No data available';
          }

          let xValue, yValue;

          if (Array.isArray(data)) {
            // If data is an array, destructure it as [x, y]
            [xValue, yValue] = data;
          } else {
            // If data is an object, assume it has `x` and `y` properties
            xValue = data?.value?.[0];
            yValue = data?.value?.[1];
          }
          const entityName = data?.entityName; // Access the entityId from the metadata
          const xAxisName = data?.xAxisName;
          const yAxisName = data?.yAxisName;

          return `
            Entity ID: ${entityName}<br/>
            ${xAxisName}: ${plotTime ? new Date(xValue)?.toLocaleString('en-US', {
            weekday: 'long', // "Monday"
            year: 'numeric', // "2025"
            month: 'long', // "March"
            day: 'numeric', // "13"
            hour: '2-digit', // "02"
            minute: '2-digit', // "30"
            second: '2-digit', // "15"
            hour12: true, // "AM/PM"
          }) : xValue}<br/>
            ${yAxisName}: ${yValue}
          `;
        },
        appendToBody: true
      },
      legend: {
        top: '7%',
        show: true,
        selectedMode: true
      },
      grid: {
        containLabel: true,
        top: '28%'
      },
      xAxis: {
        type: plotTime ? 'time' : 'value',
        // data: plotTime ? undefined : this.attributeData?.[this.chartAttributes?.xAxis?.name]?.map(item => item.value),  // x-axis values
        name: this.chartAttributes?.xAxis?.uom ? `${this.chartAttributes?.xAxis?.attributeName} (${this.chartAttributes?.xAxis?.uom})` : this.chartAttributes?.xAxis?.attributeName,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontWeight: 'bold',
          padding: [10, 0, 0, 0]
        },
        axisLabel: {
          formatter: function (value) {
            if (!plotTime) return value;

            const date = new Date(value);
            const time = date.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
            const formattedDate = date.toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric'
            });
            return `${time}\n${formattedDate}`;
          },
          hideOverlap: true
        },
        hideOverlap: true
      },
      yAxis: [
        {
          type: 'value',
          name: this.chartAttributes?.yAxis?.uom ? `${this.chartAttributes?.yAxis?.attributeName} (${this.chartAttributes?.yAxis?.uom})` : this.chartAttributes?.yAxis?.attributeName,
          position: 'left',
          axisLine: {
            show: true,
            lineStyle: {
              color: 'rgba(50, 165, 199, 1)'
            }
          },
          axisLabel: {
            formatter: '{value}'
          }
        },
        {
          type: 'value',
          name: this.chartAttributes?.yAxis2?.uom ? `${this.chartAttributes?.yAxis2?.attributeName} (${this.chartAttributes?.yAxis2?.uom})` : this.chartAttributes?.yAxis2?.attributeName,
          position: 'right',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#808080'
            }
          },
          axisLabel: {
            formatter: '{value}'
          }
        }
      ],
      toolbox: {
        left: '5%',
        top: '5%',
        show: true,
        feature: {
          dataZoom: {
            show: true,
            icon: {
              back: ' '
            }
          },
          restore: {},
          brush: {
            show: false // Disables the brush feature
          },
          ...(this.captureImage && { saveAsImage: { title: 'Download', name: this.chartAttributes?.name ?? 'Download' } }),
          ...(this.captureImage && {
            myTableSwitch: {
              show: true,
              title: 'Switch to Table',
              icon: 'path://M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z',
              onclick: (chartInstance) => {
                this.switchToTable = true;
                this.isTableLoading = true;
              }
            }
          })
        }
      },
      series: seriesData
    }
  }

  /**
 * Maps matching attributes
 * @param attributes 
 * @param attributeKey1 
 * @param attributeKey2 
 * @returns matching attributes 
 */
  private mapMatchingAttributes(
    attributes: any,
    attributeKey1: string,
    attributeKey2: string
  ): [number, number][] {
    const array1 = attributes[attributeKey1];
    const array2 = attributes[attributeKey2];
    const result: [number, number][] = [];

    if (array1 && array2) {
      const attributeMap = new Map(
        array2.map((item: any) => [item.timestamp, item.value])
      );

      array1.forEach((item1: any) => {
        const value2: number = Number(attributeMap.get(item1.timestamp));
        if (value2 !== undefined) {
          result.push([item1.value, value2]);
        }
      });
    }


    return result;
  }

  //WER Rules
  // Combine all series data and sort by x-value (time) for chronological analysis
  combineAndSortData() {
    this.combinedData = [];

    if (this.allSeriesData && this.allSeriesData?.length) {
      // Flatten all series data into a single array
      for (let i = 0; i < this.allSeriesData?.length; i++) {
        const series = this.allSeriesData[i];
        if (Array.isArray(series)) {
          for (const point of series) {
            this.combinedData.push(point);
          }
        }
      }
    }

    // Sort by x-value (time)
    // this.combinedData.sort((a, b) => a[0] - b[0]);
  }

  // Calculate statistics for the combined data
  calculateCombinedStats() {
    // Extract y-values from all points
    const yValues = this.combinedData?.map(point => point[1]);

    // Calculate mean
    const mean = this._werThresholds?.mean ?? yValues.reduce((sum, value) => sum + value, 0) / yValues.length;

    // Calculate standard deviation
    const variance = yValues.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / yValues.length;
    const stdDev = this._werThresholds?.standardDeviation ?? Math.sqrt(variance);

    return { mean, stdDev };
  }

  // Detect Western Electric rule violations for the combined data
  applyWesternElectricRulesToCombined() {
    const stats = this.calculateCombinedStats();
    const { mean, stdDev } = stats;

    // Calculate control limits
    const upperControl3 = mean + 3 * stdDev;
    const lowerControl3 = mean - 3 * stdDev;
    const upperControl2 = mean + 2 * stdDev;
    const lowerControl2 = mean - 2 * stdDev;
    const upperControl1 = mean + stdDev;
    const lowerControl1 = mean - stdDev;

    // Initialize result arrays for each rule
    const rule1Points = [];
    let rule2Points = [];
    let rule3Points = [];
    let rule4Points = [];

    // Rule 1: One point beyond 3-sigma
    for (let i = 0; i < this.combinedData.length; i++) {
      const [x, y, seriesIndex, entName, xName, yName] = this.combinedData[i];
      if (y > upperControl3 || y < lowerControl3) {
        rule1Points.push([x, y, seriesIndex, entName, xName, yName]);
      }
    }

    // Rule 2: Two out of three consecutive points beyond 2-sigma
    const uniqueRule2Points = new Map();
    for (let i = 0; i < this.combinedData.length - 2; i++) {
      const points = [
        this.combinedData[i],
        this.combinedData[i + 1],
        this.combinedData[i + 2]
      ];

      const abovePoints = [];
      const belowPoints = [];

      for (const [x, y, seriesIndex, entName, xName, yName] of points) {
        if (y > upperControl2 && y <= upperControl3) {
          abovePoints.push([x, y, seriesIndex, entName, xName, yName]);
        } else if (y < lowerControl2 && y >= lowerControl3) {
          belowPoints.push([x, y, seriesIndex, entName, xName, yName]);
        }
      }

      if (abovePoints.length >= 2) {
        for (const point of abovePoints) {
          rule2Points.push(point);
        }
      } else if (belowPoints.length >= 2) {
        for (const point of belowPoints) {
          rule2Points.push(point);
        }
      }
    }

    // Rule 3: Four out of five consecutive points beyond 1-sigma
    const uniqueRule3Points = new Map();
    for (let i = 0; i < this.combinedData.length - 4; i++) {
      const points = [this.combinedData[i], this.combinedData[i + 1], this.combinedData[i + 2], this.combinedData[i + 3], this.combinedData[i + 4]];
      let count = 0;

      let aboveCount = 0;
      let belowCount = 0;

      const abovePoints = [];
      const belowPoints = [];

      for (const [x, y, seriesIndex, entName, xName, yName] of points) {
        if (y > upperControl1) {
          abovePoints.push([x, y, seriesIndex, entName, xName, yName]);
        } else if (y < lowerControl1) {
          belowPoints.push([x, y, seriesIndex, entName, xName, yName]);
        }
      }

      // Rule 3: 4 out of 5 points beyond 1σ on the SAME side
      if (abovePoints.length >= 4) {
        for (const point of abovePoints) {
          rule3Points.push(point);
        }
      } else if (belowPoints.length >= 4) {
        for (const point of belowPoints) {
          rule3Points.push(point);
        }
      }
    }

    // Rule 4: Eight consecutive points on the same side of the center line
    const uniqueRule4Points = new Map();
    for (let i = 0; i < this.combinedData.length - 7; i++) {
      const points = this.combinedData.slice(i, i + 8);

      const allAbove = points.every(([_, y]) => y > mean);
      const allBelow = points.every(([_, y]) => y < mean);

      if (allAbove || allBelow) {
        for (const [x, y, seriesIndex, entName, xName, yName] of points) {
          rule4Points.push([x, y, seriesIndex, entName, xName, yName]);
        }
      }
    }
    // rule2Points = Array.from(uniqueRule2Points.values());
    // rule3Points =  Array.from(uniqueRule3Points.values());
    // rule4Points = Array.from(uniqueRule4Points.values());

    return {
      rule1: this.deduplicatePoints(rule1Points),
      rule2: this.deduplicatePoints(rule2Points),
      rule3: this.deduplicatePoints(rule3Points),
      rule4: this.deduplicatePoints(rule4Points),
      controlLimits: {
        mean,
        upperControl3,
        lowerControl3,
        upperControl2,
        lowerControl2,
        upperControl1,
        lowerControl1
      }
    };
  }

  deduplicatePoints(points: [number, number, number, string, string, string][]): [number, number, number, string, string, string][] {
    const seen = new Set<string>();
    return points.filter(([x, _, seriesIndex]) => {
      const key = `${x}-${seriesIndex}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Reorganize rule points by original series
  separateRulePointsBySeries(rulePoints, seriesCount) {
    const result = {};

    for (let i = 0; i < seriesCount; i++) {
      result[i] = [];
    }

    for (const [x, y, seriesIndex] of rulePoints) {
      result[seriesIndex].push([x, y]);
    }

    return result;
  }

  // Get rule description for tooltip
  getRuleDescription(ruleNum) {
    switch (ruleNum) {
      case '1': return 'Point beyond 3σ';
      case '2': return '2 of 3 points beyond 2σ';
      case '3': return '4 of 5 points beyond 1σ';
      case '4': return '8 consecutive points on same side';
      default: return 'Unknown rule';
    }
  }

  private _setScatterChartContent(chartData, plotTime) {
    const seriesData = [];
    const entityIds = [...new Set(this.pageType === 'selfService' ? this.entityId : [this.entityDetails?._id])];
    let xData;
    this.allSeriesData = [];
    let showDecimals = false;

    entityIds.forEach((entityId, i) => {
      // Find corresponding X values (TotalDistance)
      if (plotTime) {
        xData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => new Date(item.timestamp));
      } else {
        xData = this.attributeData?.[this.chartAttributes?.xAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      }

      const yData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      const allZeroDecimal = yData?.length ? yData?.every(v => v >=0 && v < 1) : false;
      if (allZeroDecimal) {
        showDecimals = true;
      }
      this.allSeriesData.push(xData?.map((x, index) => ([x, yData?.[index], i, this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId, this.chartAttributes?.xAxis?.attributeName, this.chartAttributes?.yAxis?.attributeName])));
    })

    this.combineAndSortData();
    this.combinedRuleResults = this.applyWesternElectricRulesToCombined();

    entityIds.forEach((entityId, i) => {
      // Find corresponding X values (TotalDistance)
      if (plotTime) {
        xData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => new Date(item.timestamp));
      } else {
        xData = this.attributeData?.[this.chartAttributes?.xAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      }
      // Find corresponding Y values (VehicleSpeed)
      const yData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));

      // if (xData?.length !== yData?.length) {
      //   console.error(`Data mismatch for entity ${entityId}: xData and yData lengths don't match.`);
      //   return;
      // }

      if (xData?.length === 0 || yData?.length === 0) {
        console.log(`Skipping empty data for entityId: ${entityId}`);
        return; // Skip empty data series
      }

      // Push the series data
      seriesData.push({
        type: 'scatter',
        data: xData?.map((x, index) => ({
          value: [x, yData?.[index]],
          entityId: entityId,
          entityName: this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId,
          xAxisName: this.chartAttributes?.xAxis?.attributeName,
          yAxisName: this.chartAttributes?.yAxis?.attributeName
        })),  // Combine X and Y data
        connectNulls: true,
        itemStyle: {
          color: '#1E8C9B'
        },
        markLine: {
          data: [
            ...(this.chartAttributes?.yAxis?.markers?.max
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.yAxis?.markers?.min
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.min }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.max
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.min
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.min }]
              : [])
          ],
          lineStyle: {
            type: 'solid',
            color: 'rgba(250, 174, 165, 1)',
            width: 1
          },
          symbol: 'none',
          label: {
            show: false
          },
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
        },
        symbolSize: 8,
      });

      if (this.showRules && this.showCombinedAnalysis && this.combinedRuleResults && this.showWER) {
        const limits = this.combinedRuleResults?.controlLimits;

        seriesData.push({
          name: '',
          type: 'line',
          symbol: 'none',
          lineStyle: {
            opacity: 0 // Make the line invisible
          },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: {
              color: '#333',
              type: 'dashed'
            },
            data: [
              { yAxis: limits.mean, lineStyle: { color: 'rgba(29, 29, 29, 0.4)', type: 'solid', width: 0.8 } },
              { yAxis: limits.upperControl3, lineStyle: { color: '#FF0000CF', type: 'solid', width: 0.8 } },
              { yAxis: limits.lowerControl3, lineStyle: { color: '#FF0000CF', type: 'solid', width: 0.8 } },
              { yAxis: limits.upperControl2, lineStyle: { color: '#FFBB00', type: 'solid', width: 0.8 } },
              { yAxis: limits.lowerControl2, lineStyle: { color: '#FFBB00', type: 'solid', width: 0.8 } },
              { yAxis: limits.upperControl1, lineStyle: { color: '#00D764', type: 'solid', width: 0.8 } },
              { yAxis: limits.lowerControl1, lineStyle: { color: '#00D764', type: 'solid', width: 0.8 } }
            ],
            label: {
              show: true,
              formatter: function (params) {
                if (params.value === Math.round(limits.mean * 100) / 100) return 'μ';
                if (params.value === Math.round(limits.upperControl3 * 100) / 100) return '+3σ';
                if (params.value === Math.round(limits.lowerControl3 * 100) / 100) return '-3σ';
                if (params.value === Math.round(limits.upperControl2 * 100) / 100) return '+2σ';
                if (params.value === Math.round(limits.lowerControl2 * 100) / 100) return '-2σ';
                if (params.value === Math.round(limits.upperControl1 * 100) / 100) return '+1σ';
                if (params.value === Math.round(limits.lowerControl1 * 100) / 100) return '-1σ';
                return '';
              }
            }
          }
        });
      }
    });


    if (this.showRules && this.showCombinedAnalysis && this.combinedRuleResults && this.showWER) {
      // Separate rule violations by series
      // const rule1BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule1, entityIds?.length);
      // const rule2BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule2, entityIds?.length);
      // const rule3BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule3, entityIds?.length);
      // const rule4BySeries = this.separateRulePointsBySeries(this.combinedRuleResults.rule4, entityIds?.length);

      // Add rule violation series if they contain points for this series
      // if (this.combinedRuleResults.rule1?.length > 0) {
      seriesData.push({
        name: `WER1`,
        type: 'scatter',
        symbol: 'rect',
        symbolSize: 12,
        itemStyle: {
          color: 'transparent',
          borderColor: '#CF0A0A',
          borderWidth: 2
        },
        data: this.combinedRuleResults.rule1 ?? []
      });
      // }

      // // if (this.combinedRuleResults.rule2?.length > 0) {
      // seriesData.push({
      //   name: `WER2`,
      //   type: 'scatter',
      //   symbol: 'circle',
      //   symbolSize: 18,
      //   itemStyle: {
      //     color: 'transparent',
      //     borderColor: '#3A5AFE',
      //     borderWidth: 0.8
      //   },
      //   data: this.combinedRuleResults.rule2 ?? []
      // });
      // // }

      // // if (this.combinedRuleResults.rule3?.length > 0) {
      // seriesData.push({
      //   name: `WER3`,
      //   type: 'scatter',
      //   symbol: 'circle',
      //   symbolSize: 14,
      //   itemStyle: {
      //     color: 'transparent',
      //     borderColor: '#C364C3',
      //     borderWidth: 0.8
      //   },
      //   data: this.combinedRuleResults.rule3 ?? []
      // });
      // // }

      // // if (this.combinedRuleResults.rule4?.length > 0) {
      // seriesData.push({
      //   name: `WER4`,
      //   type: 'scatter',
      //   symbol: 'circle',
      //   symbolSize: 10,
      //   itemStyle: {
      //     color: 'transparent',
      //     borderColor: '#10A957',
      //     borderWidth: 0.8
      //   },
      //   data: this.combinedRuleResults.rule4 ?? []
      // });
      // // }
    }

    //To align the chart y-axis range within max & min values
    const allYValues = this.allSeriesData?.flat()?.filter(point => Array.isArray(point) && point.length > 1)?.map(point => point[1]);
    const maxVal = Math.max(...[...allYValues, this.combinedRuleResults?.controlLimits?.upperControl3]);
    const minVal = Math.min(...[...allYValues, this.combinedRuleResults?.controlLimits?.lowerControl3]);

    const padding = (maxVal - minVal) * 0.05; // 5% of the range

    const yvaluesMax = maxVal + padding;
    const yvaluesMin = minVal - padding;

    this.chartData = {
      title: {
        text: this.chartAttributes?.name,
        top: 0,
        left: '3%',
        textStyle: {
          fontSize: 13,
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.87)',
          fontFamily: '"Roboto Flex", sans-serif'
        }
      },
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: '#A9A9A9', // Darker grey color
            width: 2, // Line width
            type: 'dashed'
          }
        },
        position: function (point) {
          const [x, y] = point;

          return [x + 30, y];  // Add 10px to the x-coordinate to move the tooltip to the right
        },
        formatter: function (params) {
          const dataPoint = params || {};
          const data = dataPoint.data || null;

          if (!data) {
            return 'No data available';
          }

          let xValue, yValue, entName, xName, yName;

          if (Array.isArray(data)) {
            // If data is an array, destructure it as [x, y]
            [xValue, yValue, entName, xName, yName] = data;
          } else {
            // If data is an object, assume it has `x` and `y` properties
            xValue = data?.value?.[0];
            yValue = data?.value?.[1];
            entName = data?.value?.[3];
            xName = data?.value?.[4];
            yName = data?.value?.[5];
          }
          const entityName = data?.entityName ?? data?.[3]; // Access the entityId from the metadata
          const xAxisName = data?.xAxisName ?? data?.[4];
          const yAxisName = data?.yAxisName ?? data?.[5];

          return `
            Entity ID: ${entityName}<br/>
            ${xAxisName}: ${plotTime ? new Date(xValue)?.toLocaleString('en-US', {
            weekday: 'long', // "Monday"
            year: 'numeric', // "2025"
            month: 'long', // "March"
            day: 'numeric', // "13"
            hour: '2-digit', // "02"
            minute: '2-digit', // "30"
            second: '2-digit', // "15"
            hour12: true, // "AM/PM"
          }) : xValue}<br/>
            ${yAxisName}: ${yValue}
          `;
        },
        appendToBody: true
      },
      legend: {
        top: '7%',
        show: true,
        selectedMode: true
      },
      xAxis: {
        type: plotTime ? 'time' : 'value',
        name: this.chartAttributes?.xAxis?.uom ? `${this.chartAttributes?.xAxis?.attributeName} (${this.chartAttributes?.xAxis?.uom})` : this.chartAttributes?.xAxis?.attributeName,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontWeight: 'bold',
          padding: [10, 0, 0, 0]
        },
        axisLabel: plotTime ? {
          formatter: function (value) {
            const date = new Date(value);
            const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
            const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
            return `${time}\n${formattedDate}`;
          },
          hideOverlap: true
        } :
          {
            formatter: (value: number) => {
              return Math.floor(value)?.toString();
            },
            textStyle: {
              fontSize: 12,
              fontFamily: 'Arial'
            },
            hideOverlap: true
          }
      },
      yAxis: {
        type: 'value',
        name: this.chartAttributes?.yAxis?.uom ? `${this.chartAttributes?.yAxis?.attributeName} (${this.chartAttributes?.yAxis?.uom})` : this.chartAttributes?.yAxis?.attributeName,
        max: yvaluesMax,
        min: yvaluesMin,
        position: 'left',
        axisLabel: {
          formatter: (value: number) => {
            return showDecimals ? value?.toFixed(2)?.toString() : Math.floor(value)?.toString();
          },
          textStyle: {
            fontSize: 12,
            fontFamily: 'Arial'
          }
        }
      },
      series: seriesData,
      toolbox: {
        left: '5%',
        top: '5%',
        show: true,
        feature: {
          dataZoom: {
            show: true,
            icon: {
              back: ' '
            }
          },
          restore: {},
          brush: {
            show: false // Disables the brush feature
          },
          ...(this.captureImage && { saveAsImage: { title: 'Download', name: this.chartAttributes?.name ?? 'Download' } }),
          ...(this.captureImage && {
            myTableSwitch: {
              show: true,
              title: 'Switch to Table',
              icon: 'path://M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z',
              onclick: (chartInstance) => {
                this.switchToTable = true;
                this.isTableLoading = true;
              }
            }
          })
        }
      },
      grid: {
        containLabel: true,
        top: '28%'
      }
    };

  }

  private _setDoubleScatterChartContent(chartData1, chartData2, plotTime) {
    const seriesData = [];
    const entityIds = [...new Set(this.pageType === 'selfService' ? this.entityId : [this.entityDetails?._id])];
    let xData;

    entityIds.forEach(entityId => {
      // Find corresponding X values (TotalDistance)
      if (plotTime) {
        xData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => new Date(item.timestamp));
      } else {
        xData = this.attributeData?.[this.chartAttributes?.xAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));
      }
      // Find corresponding Y values (VehicleSpeed)
      const yData = this.attributeData?.[this.chartAttributes?.yAxis?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));

      const y2Data = this.attributeData?.[this.chartAttributes?.yAxis2?.name]?.filter(item => item.entityId === entityId).map(item => Number(item.value));

      // if (xData?.length !== yData?.length) {
      //   console.error(`Data mismatch for entity ${entityId}: xData and yData lengths don't match.`);
      //   return;
      // }

      if (xData?.length === 0 || yData?.length === 0 || y2Data?.length === 0) {
        console.log(`Skipping empty data for entityId: ${entityId}`);
        return; // Skip empty data series
      }

      // Push the series data
      seriesData.push({
        name: this.chartAttributes?.yAxis?.uom ? `${this.chartAttributes?.yAxis?.attributeName} (${this.chartAttributes?.yAxis?.uom})` : this.chartAttributes?.yAxis?.attributeName,
        type: 'scatter',
        data: xData?.map((x, index) => ({
          value: [x, yData?.[index]],
          entityId: entityId,
          entityName: this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId,
          xAxisName: this.chartAttributes?.xAxis?.attributeName,
          yAxisName: this.chartAttributes?.yAxis?.attributeName
        })), // Combine X and Y data
        connectNulls: true,
        itemStyle: { color: '#5470C6' },
        symbolSize: 10,
        markLine: {
          data: [
            ...(this.chartAttributes?.yAxis?.markers?.max
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.yAxis?.markers?.min
              ? [{ yAxis: this.chartAttributes?.yAxis?.markers?.min }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.max
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.min
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.min }]
              : [])
          ],
          lineStyle: {
            type: 'solid',
            color: 'rgba(250, 174, 165, 1)',
            width: 1
          },
          symbol: 'none',
          label: {
            show: false
          },
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
        },
      });

      seriesData.push({
        name: this.chartAttributes?.yAxis2?.uom ? `${this.chartAttributes?.yAxis2?.attributeName} (${this.chartAttributes?.yAxis2?.uom})` : this.chartAttributes?.yAxis2?.attributeName,
        type: 'scatter',
        data: xData?.map((x, index) => ({
          value: [x, y2Data?.[index]],
          entityId: entityId,
          entityName: this.chartAttributes?.entityNames?.find(item => item?._id === entityId)?.uniqueId,
          xAxisName: this.chartAttributes?.xAxis?.attributeName,
          yAxisName: this.chartAttributes?.yAxis2?.attributeName
        })), // Combine X and Y data
        connectNulls: true,
        itemStyle: { color: '#91CC75' },
        symbolSize: 10,
        markLine: {
          data: [
            ...(this.chartAttributes?.yAxis2?.markers?.max
              ? [{ yAxis: this.chartAttributes?.yAxis2?.markers?.max }]
              : []),
            ...(this.chartAttributes?.yAxis2?.markers?.min
              ? [{ yAxis: this.chartAttributes?.yAxis2?.markers?.min }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.max
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.max }]
              : []),
            ...(this.chartAttributes?.xAxis?.markers?.min
              ? [{ xAxis: this.chartAttributes?.xAxis?.markers?.min }]
              : [])
          ],
          lineStyle: {
            type: 'solid',
            color: 'rgba(250, 174, 165, 1)',
            width: 1
          },
          symbol: 'none',
          label: {
            show: false
          },
          tooltip: {
            show: false // Disable tooltip for markPoint
          },
        },
      });
    });
    this.chartData = {
      title: {
        text: this.chartAttributes?.name,
        top: 0,
        left: '3%',
        textStyle: {
          fontSize: 13,
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.87)',
          fontFamily: '"Roboto Flex", sans-serif'
        }
      },
      xAxis: {
        type: plotTime ? 'time' : 'value',
        name: this.chartAttributes?.xAxis?.uom ? `${this.chartAttributes?.xAxis?.attributeName} (${this.chartAttributes?.xAxis?.uom})` : this.chartAttributes?.xAxis?.attributeName,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontWeight: 'bold',
          padding: [10, 0, 0, 0]
        },
        axisLabel: plotTime ? {
          formatter: function (value) {
            const date = new Date(value);
            const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
            const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
            return `${time}\n${formattedDate}`;
          },
          hideOverlap: true
        } :
          {
            formatter: (value: number) => {
              return Math.floor(value)?.toString();
            },
            textStyle: {
              fontSize: 12,
              fontFamily: 'Arial'
            },
            hideOverlap: true
          }
      },
      yAxis: [
        {
          type: 'value',
          name: this.chartAttributes?.yAxis?.uom ? `${this.chartAttributes?.yAxis?.attributeName} (${this.chartAttributes?.yAxis?.uom})` : this.chartAttributes?.yAxis?.attributeName,
          position: 'left',
          axisLine: {
            show: true,
            lineStyle: { color: '#5470C6' }
          }
        },
        {
          type: 'value',
          name: this.chartAttributes?.yAxis2?.uom ? `${this.chartAttributes?.yAxis2?.attributeName} (${this.chartAttributes?.yAxis2?.uom})` : this.chartAttributes?.yAxis2?.attributeName,
          position: 'right',
          axisLine: {
            show: true,
            lineStyle: { color: '#91CC75' }
          },
          axisLabel: {
            show: true,
            textStyle: {
              color: '#91CC75',
            }
          }
        }
      ],
      legend: {
        top: '7%',
        show: true,
        selectedMode: true
      },
      series: seriesData,
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: '#A9A9A9', // Darker grey color
            width: 2, // Line width
            type: 'dashed'
          }
        },
        position: function (point) {
          const [x, y] = point;

          return [x + 30, y];  // Add 10px to the x-coordinate to move the tooltip to the right
        },
        formatter: function (params) {
          const dataPoint = params || {};
          const data = dataPoint.data || null;

          if (!data) {
            return 'No data available';
          }

          let xValue, yValue;

          if (Array.isArray(data)) {
            // If data is an array, destructure it as [x, y]
            [xValue, yValue] = data;
          } else {
            // If data is an object, assume it has `x` and `y` properties
            xValue = data?.value?.[0];
            yValue = data?.value?.[1];
          }
          const entityName = data?.entityName; // Access the entityId from the metadata
          const xAxisName = data?.xAxisName;
          const yAxisName = data?.yAxisName;

          return `
            Entity ID: ${entityName}<br/>
            ${xAxisName}: ${plotTime ? new Date(xValue)?.toLocaleString('en-US', {
            weekday: 'long', // "Monday"
            year: 'numeric', // "2025"
            month: 'long', // "March"
            day: 'numeric', // "13"
            hour: '2-digit', // "02"
            minute: '2-digit', // "30"
            second: '2-digit', // "15"
            hour12: true, // "AM/PM"
          }) : xValue}<br/>
            ${yAxisName}: ${yValue}
          `;
        },
        appendToBody: true
      },
      grid: {
        containLabel: true,
        top: '28%'
      },
      toolbox: {
        left: '5%',
        top: '5%',
        show: true,
        feature: {
          dataZoom: {
            show: true,
            icon: {
              back: ' '
            }
          },
          restore: {},
          brush: {
            show: false // Disables the brush feature
          },
          ...(this.captureImage && { saveAsImage: { title: 'Download', name: this.chartAttributes?.name ?? 'Download' } }),
          ...(this.captureImage && {
            myTableSwitch: {
              show: true,
              title: 'Switch to Table',
              icon: 'path://M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm15 2h-4v3h4zm0 4h-4v3h4zm0 4h-4v3h3a1 1 0 0 0 1-1zm-5 3v-3H6v3zm-5 0v-3H1v2a1 1 0 0 0 1 1zm-4-4h4V8H1zm0-4h4V4H1zm5-3v3h4V4zm4 4H6v3h4z',
              onclick: (chartInstance) => {
                this.switchToTable = true;
                this.isTableLoading = true;
              }
            }
          })
        }
      },
    }
  }

  exportEvent(event: any) {
    if (event?.fileType === 'csv') {
      this.exportToExcel();
    } else {
      this.exportToJson();
    }
  }

  onTableRendered() {
    this.isTableLoading = false;
  }

  exportToExcel(): void {
    try {
      let renamedData: any[] = [];
      if (this.chartDataMaster?.length) {
        renamedData = this.chartDataMaster?.map(item => {
          const newItem: any = {};
          Object.keys(item)?.forEach(key => {
            if (this.columnHeader?.[key]) {
              newItem[this.columnHeader[key]] = item[key];
            }
          });
          return newItem;
        });
      } else {
        renamedData = [Object.fromEntries(Object.values(this.columnHeader)?.map(h => [h, '']))]
      }


      // Create worksheet from JSON
      const worksheet = XLSX.utils.json_to_sheet(renamedData);

      const headerKeys = Object.values(this.columnHeader || {});

      // Set column widths
      worksheet['!cols'] = Array(headerKeys.length)?.fill({ wch: 25 });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Telemetry Data');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `${this.chartAttributes?.name}.xlsx`);
      this.service.toasterService({ type: 'success', msg: 'Downloaded successfully!' });
    } catch (error) {
      this.service.toasterService({ type: 'error', msg: 'Failed to download file' });
    }
  }

  exportToJson(): void {
    try {
      let renamedData: any[] = [];

      if (this.chartDataMaster?.length) {
        renamedData = this.chartDataMaster?.map(item => {
          const newItem: any = {};
          Object.keys(item)?.forEach(key => {
            if (this.columnHeader?.[key]) {
              newItem[this.columnHeader[key]] = item[key];
            }
          });
          return newItem;
        })?.filter(obj => Object.keys(obj)?.length > 0);
      } else if (this.columnHeader) {
        renamedData = [Object.fromEntries(Object.values(this.columnHeader)?.map(h => [h, '']))];
      }

      const jsonString = JSON.stringify(renamedData, null, 2);

      const blob = new Blob([jsonString], { type: 'application/json' });
      saveAs(blob, `${this.chartAttributes?.name ?? 'Data'}.json`);

      this.service.toasterService({ type: 'success', msg: 'JSON downloaded successfully!' });
    } catch (error) {
      this.service.toasterService({ type: 'error', msg: 'Failed to download JSON file' });
    }
  }

}

